/**
 * AP-6 — selbstgezeichnetes Popup-Menü mit Untermenüs. Gegenstück zu
 * `PopupMenu.cpp`; Verhalten siehe FEATURE_MATRIX.md §3.6 (FM-MEN-37…41).
 *
 * Unterschied zum einfacheren `ui/controls/Popover.ts` (AP-0, ein Level, kein
 * Hover-Timing): dieses Widget bildet die Toolbar-Menüs (Decoding/View/Filter/
 * Recorder/Help) ab, inklusive verschachtelter Untermenüs (Help > Scaling/
 * Fade Speed/Theme). Häkchen werden beim ÖFFNEN aus dem aktuellen Zustand
 * gelesen (`entries()` wird bei jedem Öffnen neu aufgerufen), nicht beim Klick.
 *
 * Bewusste Abweichung (dokumentiert): das Original pollt bei 40 ms, um in
 * Win32 zuverlässig `WM_MOUSEMOVE`/Verlassen zu erkennen (FM-MEN-37) — im
 * DOM genügen `pointerenter`/`pointerleave`, ein Poll-Timer wäre unnötig.
 * Die für den Nutzer sichtbaren Zeiten (300 ms Untermenü-Öffnung, 400 ms
 * Auto-Schließen) sind 1:1 übernommen.
 */

import './PopupMenu.css';
import { createCheckboxGlyph } from './controls/Checkbox';

const SUBMENU_OPEN_DELAY_MS = 300;
const AUTO_CLOSE_DELAY_MS = 400;
/** Mindestabstand zum Fensterrand, damit nichts abgeschnitten wird. */
const MENU_EDGE_MARGIN = 4;

export interface MenuItem {
  kind?: 'item';
  label: string;
  checked?: boolean;
  disabled?: boolean;
  /** Untermenü-Einträge; wenn gesetzt, öffnet Hover statt Klick (FM-MEN-38). */
  submenu?: readonly MenuEntry[];
  /** Ignoriert, wenn `submenu` gesetzt ist — Klick auf einen Elterneintrag tut nichts. */
  onSelect?: () => void;
}

export interface MenuSeparator {
  kind: 'separator';
}

export type MenuEntry = MenuItem | MenuSeparator;

interface OpenLevel {
  root: HTMLElement;
  closeTimer: ReturnType<typeof setTimeout> | null;
}

/** Eine Menükette (Top-Level + geöffnete Untermenüs), die als Ganzes schließt. */
class MenuChain {
  private readonly levels: OpenLevel[] = [];
  private openSubmenuTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly onClosed: () => void) {}

  get depth(): number {
    return this.levels.length;
  }

  openLevel(entries: readonly MenuEntry[], anchor: HTMLElement): void {
    const level: OpenLevel = { root: this.buildLevel(entries), closeTimer: null };
    document.body.append(level.root);
    positionMenu(level.root, anchor, this.levels.length > 0);
    this.levels.push(level);
  }

  /** Schließt alle Ebenen ab (exklusive) `index`. */
  closeFrom(index: number): void {
    while (this.levels.length > index) {
      const level = this.levels.pop();
      if (level?.closeTimer !== null && level?.closeTimer !== undefined) clearTimeout(level.closeTimer);
      level?.root.remove();
    }
  }

  closeAll(): void {
    if (this.openSubmenuTimer !== null) clearTimeout(this.openSubmenuTimer);
    this.closeFrom(0);
    this.onClosed();
  }

  containsElement(node: Node): boolean {
    return this.levels.some((level) => level.root.contains(node));
  }

  private buildLevel(entries: readonly MenuEntry[]): HTMLElement {
    const menu = document.createElement('div');
    menu.className = 'cm-menu';
    menu.setAttribute('role', 'menu');
    const levelIndex = this.levels.length;

    // Auto-Schließen (FM-MEN-39): 400 ms außerhalb von Menü UND Elternbereich.
    menu.addEventListener('pointerenter', () => this.cancelCloseTimer(levelIndex));
    menu.addEventListener('pointerleave', () => this.scheduleClose(levelIndex));

    for (const entry of entries) {
      if ('kind' in entry && entry.kind === 'separator') {
        const separator = document.createElement('div');
        separator.className = 'cm-menu__sep';
        menu.append(separator);
        continue;
      }
      menu.append(this.buildItem(entry, levelIndex));
    }

    return menu;
  }

  private buildItem(item: MenuItem, levelIndex: number): HTMLElement {
    const row = document.createElement('div');
    row.className = 'cm-menu__item';
    row.setAttribute('role', 'menuitem');
    if (item.disabled) row.classList.add('cm-menu__item--disabled');

    const check = createCheckboxGlyph(item.checked ?? false);
    check.classList.add('cm-menu__check');
    check.style.visibility = item.checked ? 'visible' : 'hidden';

    const label = document.createElement('span');
    label.className = 'cm-menu__label';
    label.textContent = item.label;
    row.append(check, label);

    if (item.submenu !== undefined) {
      const arrow = document.createElement('span');
      arrow.className = 'cm-menu__arrow';
      arrow.textContent = '▶';
      row.append(arrow);

      // FM-MEN-38: öffnet nach 300 ms Hover; Klick auf den Eintrag tut nichts.
      row.addEventListener('pointerenter', () => {
        this.cancelCloseTimer(levelIndex);
        if (this.openSubmenuTimer !== null) clearTimeout(this.openSubmenuTimer);
        this.openSubmenuTimer = setTimeout(() => {
          this.closeFrom(levelIndex + 1);
          this.openLevel(item.submenu ?? [], row);
        }, SUBMENU_OPEN_DELAY_MS);
      });
      row.addEventListener('pointerleave', () => {
        if (this.openSubmenuTimer !== null) clearTimeout(this.openSubmenuTimer);
      });
    } else if (!item.disabled) {
      row.addEventListener('click', () => {
        this.closeAll();
        item.onSelect?.();
      });
    }

    return row;
  }

  private scheduleClose(levelIndex: number): void {
    const level = this.levels[levelIndex];
    if (level === undefined) return;
    level.closeTimer = setTimeout(() => {
      // Nicht schließen, wenn der Zeiger inzwischen in einer tieferen Ebene ist.
      if (this.levels.length > levelIndex + 1) return;
      this.closeAll();
    }, AUTO_CLOSE_DELAY_MS);
  }

  private cancelCloseTimer(levelIndex: number): void {
    const level = this.levels[levelIndex];
    if (level?.closeTimer !== null && level?.closeTimer !== undefined) {
      clearTimeout(level.closeTimer);
      level.closeTimer = null;
    }
  }
}

function positionMenu(menu: HTMLElement, anchor: HTMLElement, isSubmenu: boolean): void {
  const anchorRect = anchor.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();

  let left: number;
  let top: number;
  if (isSubmenu) {
    left = anchorRect.right - 2;
    top = anchorRect.top;
    if (left + menuRect.width > window.innerWidth) left = anchorRect.left - menuRect.width + 2;
  } else {
    left = anchorRect.left;
    top = anchorRect.bottom + 2;
    if (top + menuRect.height > window.innerHeight) top = anchorRect.top - menuRect.height - 2;
    // Rechts am Anker ausrichten, statt ueber den Fensterrand hinauszulaufen.
    if (left + menuRect.width > window.innerWidth - MENU_EDGE_MARGIN) {
      left = anchorRect.right - menuRect.width;
    }
  }
  const maxLeft = window.innerWidth - menuRect.width - MENU_EDGE_MARGIN;
  const maxTop = window.innerHeight - menuRect.height - MENU_EDGE_MARGIN;
  menu.style.left = `${Math.max(MENU_EDGE_MARGIN, Math.min(left, maxLeft))}px`;
  menu.style.top = `${Math.max(MENU_EDGE_MARGIN, Math.min(top, maxTop))}px`;
}

let activeChain: MenuChain | null = null;

/** Schließt ein evtl. offenes Menü — z. B. wenn ein anderer Toolbar-Button klickt. */
export function closeAllMenus(): void {
  activeChain?.closeAll();
}

function onDocumentPointerDown(event: PointerEvent): void {
  if (activeChain === null) return;
  if (event.target instanceof Node && activeChain.containsElement(event.target)) return;
  activeChain.closeAll();
}

function onKeyDown(event: KeyboardEvent): void {
  // FM-MEN-41: Tastaturnavigation existiert nicht — nur Esc schließt.
  if (event.key === 'Escape') activeChain?.closeAll();
}

document.addEventListener('pointerdown', onDocumentPointerDown);
document.addEventListener('keydown', onKeyDown);

/**
 * Erzeugt einen Toolbar-Button, der beim Klick sein Menü öffnet/schließt.
 * `getEntries` wird bei JEDEM Öffnen neu aufgerufen, damit Häkchen den
 * aktuellen Zustand zeigen (FEATURE_MATRIX.md §3: "beim Öffnen, nicht beim Klick").
 */
export function createMenuButton(label: string, getEntries: () => readonly MenuEntry[]): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'cm-menu-btn';
  button.textContent = label;

  button.addEventListener('click', (event) => {
    event.stopPropagation();
    const wasOpenForThisButton = activeChain !== null && button.classList.contains('cm-menu-btn--open');
    activeChain?.closeAll();
    if (wasOpenForThisButton) return; // zweiter Klick auf denselben Button schließt nur.

    button.classList.add('cm-menu-btn--open');
    const chain = new MenuChain(() => button.classList.remove('cm-menu-btn--open'));
    activeChain = chain;
    chain.openLevel(getEntries(), button);
  });

  return button;
}
